import { useState, useRef } from 'react';
import type { TicketStatus } from '../../types/ticket';
import './TicketActionsMenu.css';

interface TicketActionsMenuProps {
  ticketId: string;
  ticketNumber: string;
  currentStatus: TicketStatus;
  onHold?: (ticketId: string, reason?: string) => void;
  onComplete?: (ticketId: string, notes?: string) => void;
  onReopen?: (ticketId: string) => void;
}

export const TicketActionsMenu: React.FC<TicketActionsMenuProps> = ({
  ticketId,
  ticketNumber,
  currentStatus,
  onHold,
  onComplete,
  onReopen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [actionType, setActionType] = useState<'hold' | 'complete' | null>(null);
  const [reason, setReason] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMenuClick = () => {
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: 'hold' | 'complete' | 'reopen') => {
    setIsOpen(false);

    if (action === 'reopen') {
      onReopen?.(ticketId);
      return;
    }

    // For hold and complete, show reason dialog
    setActionType(action);
    setShowReasonDialog(true);
  };

  const handleReasonSubmit = () => {
    if (actionType === 'hold' && onHold) {
      onHold(ticketId, reason.trim() || undefined);
    } else if (actionType === 'complete' && onComplete) {
      onComplete(ticketId, reason.trim() || undefined);
    }

    setShowReasonDialog(false);
    setReason('');
    setActionType(null);
  };

  const handleReasonCancel = () => {
    setShowReasonDialog(false);
    setReason('');
    setActionType(null);
  };

  const handleBackdropClick = () => {
    setIsOpen(false);
  };

  // Don't show menu if no actions are available
  const hasActions = currentStatus !== 'Resolved' || onReopen;
  if (!hasActions) return null;

  return (
    <>
      {/* Menu Button */}
      <button
        ref={buttonRef}
        className="ticket-actions-btn"
        onClick={handleMenuClick}
        aria-label="More actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="ticket-actions-backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Popover Menu */}
      {isOpen && (
        <div
          className="ticket-actions-menu"
          role="menu"
          aria-labelledby="ticket-actions-button"
        >
          {currentStatus !== 'Resolved' && onHold && (
            <button
              className="ticket-actions-menu__item"
              onClick={() => handleActionClick('hold')}
              role="menuitem"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="9" r="7" />
                <rect x="6" y="6" width="6" height="6" />
              </svg>
              <span>Put Ticket on Hold</span>
            </button>
          )}

          {currentStatus !== 'Resolved' && onComplete && (
            <button
              className="ticket-actions-menu__item ticket-actions-menu__item--success"
              onClick={() => handleActionClick('complete')}
              role="menuitem"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 4.5L6.75 12.75L3 9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Mark Ticket Complete</span>
            </button>
          )}

          {currentStatus === 'Resolved' && onReopen && (
            <button
              className="ticket-actions-menu__item"
              onClick={() => handleActionClick('reopen')}
              role="menuitem"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9h12M10.5 4.5L15 9l-4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Reopen Ticket</span>
            </button>
          )}
        </div>
      )}

      {/* Reason Dialog */}
      {showReasonDialog && (
        <>
          <div
            className="reason-dialog-backdrop"
            onClick={handleReasonCancel}
            aria-hidden="true"
          />
          <div className="reason-dialog" role="dialog" aria-modal="true">
            <div className="reason-dialog__header">
              <h3 className="reason-dialog__title">
                {actionType === 'hold' ? 'Put Ticket on Hold' : 'Complete Ticket'}
              </h3>
              <p className="reason-dialog__subtitle">
                {ticketNumber}
              </p>
            </div>
            <div className="reason-dialog__content">
              <label htmlFor="action-reason" className="reason-dialog__label">
                {actionType === 'hold' ? 'Reason for Hold' : 'Completion Notes'}
                <span className="reason-dialog__optional">(Optional)</span>
              </label>
              <textarea
                id="action-reason"
                className="reason-dialog__textarea"
                placeholder={
                  actionType === 'hold'
                    ? 'E.g., Waiting for user response, need additional information...'
                    : 'E.g., Issue resolved, user confirmed fix working...'
                }
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                autoFocus
              />
              <div className="reason-dialog__hint">
                {reason.length} / 500 characters
              </div>
            </div>
            <div className="reason-dialog__actions">
              <button
                className="reason-dialog__btn reason-dialog__btn--secondary"
                onClick={handleReasonCancel}
              >
                Cancel
              </button>
              <button
                className={`reason-dialog__btn reason-dialog__btn--primary ${
                  actionType === 'complete' ? 'reason-dialog__btn--success' : ''
                }`}
                onClick={handleReasonSubmit}
                disabled={reason.length > 500}
              >
                {actionType === 'hold' ? 'Put on Hold' : 'Mark Complete'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
